import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { IMAGE_QUALITY, isImage } from '@obraplan/core/domain/attachments/attachment';
import { isEditable } from '@obraplan/core/domain/planning/daily-report';
import type { CrewCount, EquipmentLine } from '@obraplan/core/domain/planning/daily-report-crew';
import type { DailyReportTask } from '@obraplan/core/domain/planning/daily-report-task';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import { RDO_NA_FILA, type ConferenceOperation } from '@/app/outbox-types';
import { EmptyState, LoadingState } from '@/components/feedback/states';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import { AttachmentGallery } from './AttachmentGallery';
import { DailyReportCrew, DailyReportEquipment } from './DailyReportCrew';
import { DailyReportHeader } from './DailyReportHeader';
import { ReportSection } from './DailyReportSection';
import { DailyReportQuality } from './DailyReportQuality';
import { DailyReportNotes, DailyReportTasks } from './DailyReportTasks';
import { DailyReportWeather, type ShiftInput } from './DailyReportWeather';
import { AvisoDaFila } from './AvisoDaFila';
import type { TrocaDeAtividade } from './DailyReportWork';
import { DailyReportWorkSection } from './DailyReportWorkSection';
import { GUARDADO_NO_APARELHO, syncDoCartao } from './fila-da-conferencia';
import {
  documentoComAFila,
  efetivoComAFila,
  emissaoPendente,
  filaDoRdo,
  PENDING_REPORT_ID,
} from './fila-da-equipe-e-do-rdo';
import { estaNaFila, galeriaComAFila, removerAFoto, urlDaFotoNaFila } from './fotos-na-fila';
import { useFilaPorCima } from './use-fila-por-cima';
import {
  emitirPdfDoRelatorio,
  FORMATOS_DE_FOTO_DO_PAPEL,
  type ReportPhoto,
} from './daily-report-pdf';

/** O aviso da emissão feita sem sinal (RN-624). */
export const EMISSAO_GUARDADA =
  'Emissão guardada no aparelho — o número e o PDF saem quando ela subir.';

/**
 * O Relatório Diário de Obra do dia aberto — as seções, as leituras e as escritas.
 *
 * Mora dentro da `/conferencia`, e não numa tela nova: o RDO é a conferência vista de cima
 * (ADR-121). A conferência continua sendo por pessoa e o documento continua sendo por
 * obra e por dia; o que a tela faz é mostrar os dois recortes do mesmo dia.
 *
 * ## Três leituras, e por que não é uma só
 *
 * ```text
 *   getDailyReport        o documento: número, estado, clima, tarefa avulsa, equipamento
 *   getDailyReportTasks   as frentes do dia, derivadas das conferências
 *   getDailyReportCrew    o efetivo contado, fundido com o corrigido
 * ```
 *
 * As duas últimas são **derivadas** e não moram no documento (ADR-122): elas são montadas
 * do estado atual das conferências toda vez que alguém as pede. É isso que faz reabrir uma
 * conferência mudar o RDO — e é por isso que reabrir também o marca como desatualizado
 * (RN-210), em vez de deixar a divergência acontecer em silêncio.
 *
 * ## A leitura não cria o documento
 *
 * `getDailyReport` só procura. Quem cria é `getOrOpenDailyReport`, no botão "Iniciar o
 * relatório do dia", e qualquer gravação de conteúdo. Fosse a leitura a criar, folhear
 * trinta dias de agosto abriria trinta rascunhos e escreveria trinta `REPORT_OPEN` na
 * auditoria — e recusaria a tela inteira a quem tem `employees.view` e não tem
 * `labor.confirm`, que é quem mais olha o RDO sem preencher.
 */
export function DailyReportPanel({
  projectId,
  date,
  dateLabel,
  conference,
  lines,
  troca,
  onEncerrarTroca,
}: {
  projectId: ProjectId;
  /** `AAAA-MM-DD` — o dia que a barra de navegação da conferência escolheu. */
  date: string;
  /** `quinta-feira, 10/09/2026` — o mesmo rótulo da tela vai para o papel. */
  dateLabel: string;
  /** Os cartões da conferência, com filtros — a página os monta porque tem os diálogos deles. */
  conference: ReactNode;
  /** As linhas do dia como o servidor as leu, para a hora extra do lançamento (RN-641). */
  lines?: readonly InspectionLine[];
  /** A atividade errada que o "Trocar" do dia da pessoa mandou corrigir (RN-645). */
  troca?: TrocaDeAtividade | undefined;
  onEncerrarTroca?: (() => void) | undefined;
}) {
  const container = useContainer();
  const { session } = useSessionState();
  const canConfirm = usePermission(PERMISSION.LABOR_CONFIRM);
  const canModify = usePermission(PERMISSION.LABOR_MODIFY);

  /** A legenda da próxima foto (RN-214). Mora aqui porque é da galeria, e não do anexo. */
  const [legenda, setLegenda] = useState('');
  const [gerandoPdf, setGerandoPdf] = useState(false);

  const pronto = session !== undefined;
  const chave = [session?.userId, projectId, date];

  const report = useLive(
    () =>
      pronto
        ? container.getDailyReport.execute({ projectId, date }, session)
        : Promise.resolve(undefined),
    chave,
  );

  const tasks = useLive(
    () =>
      pronto
        ? container.getDailyReportTasks.execute({ projectId, date }, session)
        : Promise.resolve(undefined),
    chave,
  );

  const crew = useLive(
    () =>
      pronto
        ? container.getDailyReportCrew.execute({ projectId, date }, session)
        : Promise.resolve(undefined),
    chave,
  );

  const project = useLive(
    () => (pronto ? container.getProject.execute(projectId, session) : Promise.resolve(undefined)),
    [session?.userId, projectId],
  );

  // A fila do aparelho neste dia (Fase 43, etapa 3, RN-612): o documento já mostra o que foi
  // gravado sem sinal, e a marca diz que ainda não chegou ao servidor.
  const fila = useLive(
    () =>
      session === undefined
        ? Promise.resolve([])
        : container.outbox.list(session.userId, { projectId, date }),
    chave,
  );
  const entradasDoDocumento = useFilaPorCima(fila, report);
  const entradasDoEfetivo = useFilaPorCima(fila, crew);
  const documento = useMemo(
    () =>
      report === undefined || !report.ok
        ? undefined
        : documentoComAFila(report.value, entradasDoDocumento, { projectId, date }),
    [report, entradasDoDocumento, projectId, date],
  );
  const syncDoRdo = syncDoCartao(filaDoRdo(entradasDoDocumento));
  // A emissão feita sem sinal espera o número do servidor (RN-624): sem ele, não há PDF.
  const emitindo = emissaoPendente(entradasDoDocumento);
  /** Grava agora, ou guarda no aparelho quando não há sinal (RN-603). */
  const submeter = useCallback(
    (operacao: ConferenceOperation) => container.outbox.submit(operacao, session!),
    [container, session],
  );
  const doDia = { projectId, date, resourceId: RDO_NA_FILA } as const;

  const photos = useLive(
    () =>
      session === undefined || documento === undefined || documento.id === PENDING_REPORT_ID
        ? Promise.resolve(undefined)
        : container.listDailyReportPhotos.execute({ reportId: documento.id }, session),
    [session?.userId, documento?.id],
  );

  // O binário só é lido aqui, um anexo por vez, e quem revoga a URL é a galeria. A foto que
  // ainda está na fila sai do próprio arquivo, sem rede (RN-622).
  const resolveAttachmentUrl = useCallback(
    async (attachmentId: string): Promise<string | undefined> => {
      if (estaNaFila(attachmentId)) return urlDaFotoNaFila(attachmentId, fila ?? []);
      if (session === undefined) return undefined;
      const result = await container.getAttachmentUrl.execute({ attachmentId }, session);
      return result.ok ? result.value : undefined;
    },
    [container, session, fila],
  );

  // Emitir e revisar entram na fila do RDO (RN-624, RN-625), depois do que foi gravado nele: sem
  // sinal, o documento fecha na hora e o número vem do servidor quando a emissão subir.
  const [emitir, emitirState] = useCommand(() => submeter({ kind: 'rdo-emitir', ...doDia }), {
    successMessage: (resultado) => {
      if (resultado.queued) return EMISSAO_GUARDADA;
      const emitido = resultado.outcome.report;
      if (emitido === undefined) return 'RDO emitido.';
      return emitido.revision > 1
        ? `RDO nº ${String(emitido.number)} · revisão ${String(emitido.revision)} emitida.`
        : `RDO nº ${String(emitido.number)} emitido.`;
    },
  });

  const [revisar, revisarState] = useCommand(() => submeter({ kind: 'rdo-revisar', ...doDia }), {
    successMessage: (resultado) =>
      resultado.queued
        ? GUARDADO_NO_APARELHO
        : 'Revisão aberta. O documento voltou a aceitar alteração.',
  });

  const [gravarClima, climaState] = useCommand(
    (shifts: readonly ShiftInput[]) => submeter({ kind: 'rdo-clima', ...doDia, shifts }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Tempo e jornada gravados.',
      silent: true,
    },
  );

  const [gravarTarefas, tarefasState] = useCommand(
    (tasks_: readonly DailyReportTask[]) =>
      submeter({ kind: 'rdo-tarefas', ...doDia, tasks: tasks_ }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Tarefas avulsas gravadas.',
      silent: true,
    },
  );

  const [gravarObservacao, observacaoState] = useCommand(
    (notes: string) => submeter({ kind: 'rdo-ocorrencias', ...doDia, notes }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Ocorrências do dia gravadas.',
      silent: true,
    },
  );

  const [gravarEfetivo, efetivoState] = useCommand(
    (crew_: readonly CrewCount[]) => submeter({ kind: 'rdo-efetivo', ...doDia, crew: crew_ }),
    {
      successMessage: (resultado) => (resultado.queued ? GUARDADO_NO_APARELHO : 'Efetivo gravado.'),
      silent: true,
    },
  );

  const [gravarEquipamento, equipamentoState] = useCommand(
    (equipment: readonly EquipmentLine[]) =>
      submeter({ kind: 'rdo-equipamento', ...doDia, equipment }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Equipamento gravado.',
      silent: true,
    },
  );

  const [anexarFoto, anexoState] = useCommand(
    (input: { reportId: string | undefined; files: readonly File[]; caption: string }) =>
      /*
       * A foto precisa de um dono com id, e o dono é o rascunho. Antes havia um botão
       * "Iniciar o relatório do dia" para criá-lo; agora o primeiro gesto que precisa do
       * documento o cria — e, sem sinal, a operação o abre quando subir (RN-612).
       */
      submeter({
        kind: 'rdo-fotos',
        ...doDia,
        ...(input.reportId === undefined || input.reportId === PENDING_REPORT_ID
          ? {}
          : { reportId: input.reportId }),
        files: input.files,
        caption: input.caption,
      }),
    {
      successMessage: (resultado) => (resultado.queued ? GUARDADO_NO_APARELHO : 'Foto anexada.'),
      silent: true,
      onSuccess: () => setLegenda(''),
    },
  );

  // Na chave do RDO (RN-621): a foto do documento emitido só sai depois da revisão que o reabre.
  const [removerFoto] = useCommand(
    (attachmentId: string) => removerAFoto(container.outbox, session!, attachmentId, doDia),
    {
      successMessage: (resultado) => (resultado.queued ? GUARDADO_NO_APARELHO : 'Foto removida.'),
      silent: true,
    },
  );

  /**
   * Gera o PDF do dia.
   *
   * Não é um `useCommand`: não há caso de uso, não há escrita e não há nada a revalidar —
   * o documento é montado do que a tela já leu. O único trabalho novo é o binário das
   * fotos, que precisa virar `data:` porque o `pdfmake` embute a imagem no arquivo e uma
   * `blob:` morre junto com a aba.
   *
   * As fotos são lidas **uma a uma, em ordem**, e a que falhar é pulada: uma imagem que
   * sumiu do armazenamento não pode impedir o documento inteiro de sair.
   */
  const baixarPdf = async (): Promise<void> => {
    if (documento === undefined || session === undefined) return;
    const obra = project !== undefined && project.ok ? project.value : undefined;
    if (obra === undefined) return;

    setGerandoPdf(true);
    try {
      const fotos: ReportPhoto[] = [];
      for (const anexo of photos !== undefined && photos.ok ? photos.value.attachments : []) {
        // O PDF anexado à galeria não é foto: não há como desenhá-lo na grade do papel.
        if (anexo.caption === undefined || !isImage(anexo.mimeType)) continue;
        const dataUrl = await lerComoDataUrl(await resolveAttachmentUrl(anexo.id));
        if (dataUrl === undefined) continue;
        fotos.push({ dataUrl, caption: anexo.caption });
      }

      await emitirPdfDoRelatorio({
        project: obra,
        report: documento,
        dateLabel,
        fronts: tasks !== undefined && tasks.ok ? tasks.value.fronts : [],
        quality: tasks !== undefined && tasks.ok ? tasks.value.quality : [],
        crew: crew !== undefined && crew.ok ? crew.value.crew : [],
        photos: fotos,
      });
    } catch (causa) {
      console.error('[obraplan] falha ao gerar o PDF do RDO', causa);
      toast.error('Não foi possível gerar o PDF deste relatório.');
    } finally {
      setGerandoPdf(false);
    }
  };

  if (report === undefined || tasks === undefined || crew === undefined) {
    return <LoadingState label="Carregando o relatório do dia…" />;
  }

  if (!report.ok) {
    return (
      <EmptyState
        title="O relatório deste dia não pôde ser lido"
        description={report.error.message}
      />
    );
  }

  // Documento emitido recusa toda escrita de conteúdo (RN-210). O `undefined` é o dia que
  // ainda não tem documento nenhum, e nele as seções são editáveis: a primeira gravação
  // cria o rascunho.
  const editavel = canConfirm && (documento === undefined || isEditable(documento));

  return (
    <div className="flex flex-col gap-4">
      <DailyReportHeader
        project={project !== undefined && project.ok ? project.value : undefined}
        date={date}
        report={documento}
        canConfirm={canConfirm}
        canModify={canModify}
        isPending={emitirState.isPending || revisarState.isPending}
        emissionPending={emitindo}
        isGeneratingPdf={gerandoPdf}
        onClose={() => void emitir()}
        onRevise={() => void revisar()}
        onDownloadPdf={() => void baixarPdf()}
      />

      {/* A fila do aparelho no documento do dia (Fase 43, etapa 3, RN-612). */}
      {syncDoRdo !== undefined && (
        <AvisoDaFila
          sync={syncDoRdo}
          pendente={
            syncDoRdo.count === 1
              ? '1 alteração do RDO aguardando envio'
              : `${String(syncDoRdo.count)} alterações do RDO aguardando envio`
          }
          recusado="O servidor não aceitou uma alteração do RDO feita sem sinal"
        />
      )}

      {/* Fase 20 — quem fez o quê vem antes do tempo e das fotos: é a primeira pergunta do dia. */}
      <DailyReportWorkSection
        projectId={projectId}
        date={date}
        editable={editavel}
        tasks={documento?.tasks ?? []}
        conference={conference}
        {...(lines === undefined ? {} : { lines })}
        troca={troca}
        onEncerrarTroca={onEncerrarTroca}
      />

      <DailyReportWeather
        shifts={documento?.shifts ?? []}
        editable={editavel}
        isSaving={climaState.isPending}
        errorMessage={climaState.error?.message}
        onSave={(shifts) => void gravarClima(shifts)}
      />

      <DailyReportTasks
        fronts={tasks.ok ? tasks.value.fronts : []}
        tasks={documento?.tasks ?? []}
        editable={editavel}
        isSaving={tarefasState.isPending}
        errorMessage={tarefasState.error?.message}
        onSave={(lista) => void gravarTarefas(lista)}
      />

      <DailyReportQuality quality={tasks.ok ? tasks.value.quality : []} editable={editavel} />

      <DailyReportCrew
        crew={crew.ok ? efetivoComAFila(crew.value.crew, entradasDoEfetivo) : []}
        editable={editavel}
        isSaving={efetivoState.isPending}
        errorMessage={efetivoState.error?.message}
        onSave={(lista) => void gravarEfetivo(lista)}
      />

      <DailyReportEquipment
        equipment={documento?.equipment ?? []}
        editable={editavel}
        isSaving={equipamentoState.isPending}
        errorMessage={equipamentoState.error?.message}
        onSave={(lista) => void gravarEquipamento(lista)}
      />

      <DailyReportNotes
        notes={documento?.notes}
        editable={editavel}
        isSaving={observacaoState.isPending}
        errorMessage={observacaoState.error?.message}
        onSave={(texto) => void gravarObservacao(texto)}
      />

      <ReportSection
        title="Relatório fotográfico"
        description="A segunda página do documento. Cada foto sai com a legenda embaixo — é ela que diz o que se está vendo a quem não esteve no canteiro."
        editable={editavel}
        isSaving={anexoState.isPending}
        errorMessage={anexoState.error?.message}
      >
        <AttachmentGallery
          {...galeriaComAFila(
            photos !== undefined && photos.ok ? photos.value : undefined,
            fila ?? [],
            {
              kind: 'rdo',
              date,
            },
          )}
          canAttach={editavel}
          canRemove={editavel}
          isPending={anexoState.isPending}
          caption={{ value: legenda, onChange: setLegenda }}
          resolveUrl={resolveAttachmentUrl}
          onAttach={(files) => {
            void anexarFoto({ reportId: documento?.id, files, caption: legenda });
          }}
          onRemove={(attachmentId) => void removerFoto(attachmentId)}
        />
      </ReportSection>
    </div>
  );
}

/**
 * A `blob:` da galeria vira `data:`.
 *
 * O `pdfmake` embute a imagem dentro do arquivo, e uma URL de objeto é um ponteiro para a
 * memória da aba: o PDF sairia com moldura vazia, ou nem sairia. A URL é revogada aqui
 * mesmo — sem isso, gerar o PDF de um dia com dez fotos deixaria dez blobs vivos até a aba
 * fechar, que é o mesmo cuidado que a galeria toma (ADR-109).
 *
 * O `pdfmake` só lê JPEG e PNG. O WebP que a compressão não encolheu é redesenhado aqui
 * como JPEG, para a foto continuar no papel; o que nem assim vira JPEG é pulado, como a
 * foto que sumiu do armazenamento.
 */
async function lerComoDataUrl(url: string | undefined): Promise<string | undefined> {
  if (url === undefined) return undefined;
  try {
    const resposta = await fetch(url);
    const lido = await resposta.blob();
    const blob = FORMATOS_DE_FOTO_DO_PAPEL.has(lido.type) ? lido : await emJpeg(lido);
    if (blob === undefined) return undefined;
    return await new Promise<string | undefined>((resolver) => {
      const leitor = new FileReader();
      leitor.onload = () => resolver(typeof leitor.result === 'string' ? leitor.result : undefined);
      leitor.onerror = () => resolver(undefined);
      leitor.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A imagem que o `pdfmake` não lê, redesenhada pelo canvas do navegador como JPEG. */
async function emJpeg(imagem: Blob): Promise<Blob | undefined> {
  if (!imagem.type.startsWith('image/') || typeof createImageBitmap !== 'function') {
    return undefined;
  }
  const bitmap = await createImageBitmap(imagem);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const contexto = canvas.getContext('2d');
    if (contexto === null) return undefined;
    contexto.drawImage(bitmap, 0, 0);
    const jpeg = await new Promise<Blob | null>((resolver) => {
      canvas.toBlob(resolver, 'image/jpeg', IMAGE_QUALITY);
    });
    return jpeg ?? undefined;
  } finally {
    bitmap.close();
  }
}
